import { Loader2, Sparkles, Lock, ArrowRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useLearningEngine } from "@/hooks/useLearningEngine";
import { actionCtaLabel, HEIRWISE_ENGINE_NAME } from "@/lib/learning-engine/engineRules";

/**
 * EngineStatusCard
 *
 * The authoritative "next best action" surface on the Dashboard. All
 * routing, locking, and CTA labeling come from `resolve_next_learning_action`.
 * The Launch Path list view remains below this card as a progress overview,
 * but this card is the source of truth for what the learner should do next.
 */
export function EngineStatusCard() {
  const navigate = useNavigate();
  const { nextAction, isLoading, recordEvent } = useLearningEngine();

  if (isLoading) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 flex items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Consulting the {HEIRWISE_ENGINE_NAME}…
      </div>
    );
  }

  if (!nextAction) return null;

  const isSafeLocked = nextAction.action === "error_safe_locked";
  const isLocked = nextAction.is_locked || isSafeLocked;

  const handleContinue = async () => {
    await recordEvent({
      eventType: "next_action_resolved",
      moduleKey: nextAction.target_module_key,
      lessonId: nextAction.target_lesson_id && /^[0-9a-f-]{36}$/i.test(nextAction.target_lesson_id)
        ? nextAction.target_lesson_id
        : null,
      metadata: {
        action: nextAction.action,
        audit_code: nextAction.audit_code,
      },
    });
    if (isLocked || !nextAction.target_route) return;
    navigate(nextAction.target_route);
  };

  return (
    <section
      aria-label="HeirWise Adaptive Learning Engine — Your next best action"
      className={`rounded-xl border p-5 sm:p-6 space-y-4 ${
        isLocked
          ? "border-amber-500/40 bg-amber-500/5"
          : "border-primary/30 bg-gradient-to-br from-primary/5 to-transparent"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            {isLocked ? (
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            ) : (
              <Sparkles className="w-3.5 h-3.5 text-primary" />
            )}
            <span>Your Next Best Action</span>
          </div>
          <h2 className="font-heading text-lg sm:text-xl font-semibold text-foreground">
            {isLocked ? "Progress review needed" : actionCtaLabel(nextAction)}
          </h2>
        </div>
      </div>

      <p className="text-sm text-foreground/80 leading-relaxed">{nextAction.reason}</p>

      {nextAction.lock_reason && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
          <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{nextAction.lock_reason}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <Button
          onClick={handleContinue}
          disabled={isLocked || !nextAction.target_route}
          className="gap-2"
          size="lg"
        >
          {actionCtaLabel(nextAction)}
          {!isLocked && <ArrowRight className="w-4 h-4" />}
        </Button>
        <span
          className="text-[10px] font-mono text-muted-foreground/70"
          title={`Decision audit code from ${HEIRWISE_ENGINE_NAME}`}
        >
          {nextAction.audit_code}
        </span>
      </div>
    </section>
  );
}

export default EngineStatusCard;